import { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { Phone, Instagram, Linkedin, Globe } from 'lucide-react';
import { aboutContent, teamMembers } from '../data/mock';
import ChromaGrid from '../components/animations/ChromaGrid';
import SimpleHeroSection from '../components/simpleHero';
import './About.css';

const About = () => {
  const philosophyRef = useRef(null);
  const teamRef = useRef(null);
  const missionRef = useRef(null);

  const isPhilosophyInView = useInView(philosophyRef, { once: true, margin: '-100px' });
  const isTeamInView = useInView(teamRef, { once: true, margin: '-100px' });
  const isMissionInView = useInView(missionRef, { once: true, margin: '-100px' });

  // Founder contact info
  const founderInfo = {
    Vijay: {
      name: 'Vijay',
      phone: '8667273422',
      instagram: 'https://www.instagram.com/naan_vijayanarayanan/',
      linkedin: 'https://www.linkedin.com/in/vijaya-narayanan/'
    },
    'Sanjay': {
      name: 'Sanjay',
      phone: '7200809026',
      instagram: 'https://www.instagram.com/tech_lifter__._._?igsh=MXVmdzMybHF4emFsMQ==',
      portfolio: 'https://sanjay-portfollio.netlify.app/'
    }
  };

  const renderFounderLinks = (founderData) => (
    <div className="founder-social-links">
      <a className="founder-social-link" href={`tel:${founderData.phone}`} aria-label={`Call ${founderData.name}`}>
        <Phone size={18} />
      </a>
      {founderData.instagram && (
        <a
          className="founder-social-link"
          href={founderData.instagram}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${founderData.name} Instagram`}
        >
          <Instagram size={18} />
        </a>
      )}
      {founderData.linkedin && (
        <a
          className="founder-social-link"
          href={founderData.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${founderData.name} LinkedIn`}
        >
          <Linkedin size={18} />
        </a>
      )}
      {founderData.portfolio && (
        <a
          className="founder-social-link"
          href={founderData.portfolio}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${founderData.name} portfolio`}
        >
          <Globe size={18} />
        </a>
      )}
    </div>
  );

  // Reorder founders: Vijay first, then Sanjay
  const orderedFounders = [
    teamMembers.find(m => m.name === "Vijay"),
    teamMembers.find(m => m.name === "Sanjay")
  ].filter(Boolean);

  return (
    <motion.div
      className="about-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section - Updated with Services page style */}
      <SimpleHeroSection
        title="About TÆKNI"
        tagline="A digital studio where innovation meets execution"
      />
      {/* About TAEKNI */}
      <section ref={philosophyRef} className="about-philosophy">
        <div className="about-container">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={isPhilosophyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="section-title">{aboutContent.philosophy.title}</h2>
            <p className="section-content-philosophy">{aboutContent.philosophy.content}</p>
          </motion.div>
        </div>
      </section>

      {/* Meet the Founders */}
      <section ref={teamRef} className="about-team">
        <div className="about-container">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 60 }}
            animate={isTeamInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            Meet the Founders
          </motion.h2>
          <div className="founders-container">
            {orderedFounders.map((member, index) => {
              const isReverse = index % 2 === 1; // Sanjay (index 1) will have reversed layout
              const founderData = founderInfo[member.name];
              return (
                // <Magnetic key={member.id} strength={0.2}>
                  <motion.div
                    className={`founder-item ${isReverse ? 'founder-reverse' : ''}`}
                    initial={{ opacity: 0, y: 60 }}
                    animate={isTeamInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                  >
                    <div className="founder-card">
                    {isReverse ? (
                      <>
                        <div className="team-info">
                          <p className="team-eyebrow">Founder spotlight</p>
                          <h3 className="team-name">{member.name}</h3>
                          <p className="team-role">{member.role}</p>
                          <p className="team-bio">{member.bio}</p>
                          {founderInfo[member.name] && renderFounderLinks(founderInfo[member.name])}
                        </div>
                        <div className="team-image-wrapper">
                          <ChromaGrid
                            items={[{
                              image: member.image,
                              borderColor: '#FAFF00',
                              gradient: 'linear-gradient(145deg, #FAFF00, #000)'
                            }]}
                            columns={1}
                            rows={1}
                            radius={400}
                            className="founder-chroma"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="team-image-wrapper">
                          <ChromaGrid
                            items={[{
                              image: member.image,
                              borderColor: '#FAFF00',
                              gradient: 'linear-gradient(145deg, #FAFF00, #000)'
                            }]}
                            columns={1}
                            rows={1}
                            radius={400}
                            className="founder-chroma"
                          />
                        </div>
                        <div className="team-info">
                          <p className="team-eyebrow">Founder spotlight</p>
                          <h3 className="team-name">{member.name}</h3>
                          <p className="team-role">{member.role}</p>
                          <p className="team-bio">{member.bio}</p>
                          {founderInfo[member.name] && renderFounderLinks(founderInfo[member.name])}
                        </div>
                      </>
                    )}
                  </div>
                  </motion.div>
                // </Magnetic>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section ref={missionRef} className="about-mission">
        <div className="about-container">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={isMissionInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="mission-vision-grid">
              {/* <Magnetic strength={0.2}> */}
                <motion.div
                  className="mission-vision-item"
                  initial={{ opacity: 0, y: 40 }}
                  animate={isMissionInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <h3 className="mission-vision-title">Mission</h3>
                  <p className="mission-vision-content">
                    Our mission is to engineer purposeful digital experiences that bridge design, technology, and real-world impact. At TAEKNI, we strive to build solutions that are scalable, efficient, and deeply aligned with user needs—empowering businesses to grow through clarity, performance, and thoughtful innovation rather than complexity.
                  </p>
                </motion.div>
              {/* </Magnetic> */}
              {/* <Magnetic strength={0.2}> */}
                <motion.div
                  className="mission-vision-item"
                  initial={{ opacity: 0, y: 40 }}
                  animate={isMissionInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3 className="mission-vision-title">Vision</h3>
                  <p className="mission-vision-content">
                    Our vision is to shape a future where technology feels seamless, human, and empowering. TAEKNI aims to become a trusted innovation partner, recognized for building long-lasting digital ecosystems that evolve with change while remaining grounded in usability, integrity, and intelligent design.
                  </p>
                </motion.div>
              {/* </Magnetic> */}
            </div>
          </motion.div>
        </div>
      </section>

    </motion.div>
  );
};

export default About;